import React, { useEffect, useRef, useState } from 'react';
type ProductVideoProps = {
  src: string;
  label: string;
  className?: string;
};

/**
 * Always-silent looping product video. It only starts downloading when it
 * gets close to the screen (keeps the first load fast), and audio is forced
 * off on mount and re-forced if anything tries to unmute it.
 */
export function ProductVideo({
  src,
  label,
  className = ''
}: ProductVideoProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const forceSilent = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = true;
    video.defaultMuted = true;
    video.volume = 0;
  };

  // Defer loading until the video is ~400px from the viewport
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    forceSilent();
    if (!('IntersectionObserver' in window)) {
      setShouldLoad(true);
      return;
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setShouldLoad(true);
        observer.disconnect();
      }
    }, {
      rootMargin: '400px 0px'
    });
    observer.observe(video);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    const video = videoRef.current;
    if (!shouldLoad || !video) return;
    forceSilent();
    const attempt = video.play();
    if (attempt && typeof attempt.catch === 'function') {
      attempt.catch(() => {
        // Autoplay blocked (e.g. low-power mode) — the first frame stays visible.
      });
    }
  }, [shouldLoad]);
  return <video ref={videoRef} src={shouldLoad ? src : undefined} aria-label={label} autoPlay loop muted playsInline preload={shouldLoad ? 'auto' : 'none'} controls={false} disablePictureInPicture disableRemotePlayback onVolumeChange={forceSilent} onLoadedMetadata={forceSilent} className={className} data-id="element-376" data-mp="3ho2sv6au8zj1" />;
}